import React, { useMemo, useState } from 'react'
import {
    Text,
    Box,
    Grid,
    useBreakpointValue,

    /* Hooks */
    useMultiStyleConfig,
    useTheme
} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation } from 'swiper/modules'
import 'swiper/css'

/* Utils */
import { renderComponentsSwitch } from '../../utils/contentRenderer'
import { generateStyles, getHideStyles } from '../../utils/utils'

/* Icons */
import { ChevronLeftIcon, ChevronRightIcon } from '../../../icons/index'

const CtaItemListBlock = ({
    CtaItems,
    Title,
    Subtitle,
    CtaItemListBlockTitleStyle,
    CtaItemListBlockSubtitleStyle,
    CTABackgroundColor,
    CTABackgroundImage,
    isLandingPage = false,
    CtaDivider = false,
    HideOnDevices,
    DisplayOption,
    Gap,
    CtaGap,
    ...props
}) => {
    const hideStyles = getHideStyles(HideOnDevices)

    if (hideStyles.doNotLoadComponent) return

    const theme = useTheme()
    const lgBreakpoint = parseFloat(theme.breakpoints.lg) * 16
    const global = useMultiStyleConfig('GlobalCMS')
    const styles = useMultiStyleConfig('CtaItemListBlock', {
        ...props
    })
    const [spaceBetween, setSpaceBetween] = useState({})
    const isMobile = useBreakpointValue({base: true, lg: false})

    // Use CtaGap or Gap from CMS - convert to px
    const gapValue = CtaGap ?? Gap

    // Auto-detect if should use landing page mode based on Icon With Text styling
    const hasIconWithText = CtaItems?.some((item) => {
        const itemData = item?.Expanded || item
        return itemData?.Styling === 'Icon With Text'
    })
    const shouldUseLandingPageLayout = isLandingPage || hasIconWithText

    // CtaDivider Grid Layout
    if (CtaDivider) {
        const hasBackground = CTABackgroundColor && CTABackgroundColor !== ''
        return (
            <Box
                {...hideStyles}
                data-block-type="CtaItemListBlock"
                backgroundColor={hasBackground ? CTABackgroundColor : 'transparent'}
                py={{ base: hasBackground ? 6 : 0, lg: hasBackground ? 8 : 0 }}
                px={{ base: 4, lg: 0 }}
                mb={{ base: 0, lg: hasBackground ? 0 : 12 }}
            >
                <Box {...global.notFullBleed} marginRight={0}>
                    {Title && (
                        <Text {...generateStyles(CtaItemListBlockTitleStyle, { ...styles.title }, { h2: { base: '20px', lg: '32px' } })} mb={{ base: 4, lg: 6 }}>
                            {Title}
                        </Text>
                    )}
                    {Subtitle && (
                        <Text {...generateStyles(CtaItemListBlockSubtitleStyle, { ...styles.subtitle })} mb={{ base: 6, lg: 8 }}>
                            {Subtitle}
                        </Text>
                    )}
                    <Grid
                        templateColumns={{ base: '1fr', lg: `repeat(${CtaItems?.length || 1}, 1fr)` }}
                        width="100%"
                        gap={gapValue != null ? `${gapValue}px` : 0}
                    >
                        {CtaItems?.map((CtaItemBlock, index) => {
                            const isLastItem = index === CtaItems.length - 1
                            return (
                                <Box
                                    key={`cta-item-${index}`}
                                    borderRight={{ base: 'none', lg: isLastItem ? 'none' : '1px solid black' }}
                                    borderBottom={{ base: isLastItem ? 'none' : '1px solid black', lg: 'none' }}
                                    px={{ base: 0, lg: 6 }}
                                    py={{ base: 4, lg: 0 }}
                                    minWidth={0}
                                    overflow="hidden"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                >
                                    {renderComponentsSwitch(CtaItemBlock?.Expanded)}
                                </Box>
                            )
                        })}
                    </Grid>
                </Box>
            </Box>
        )
    }

    useMemo(() => {
        const ctaStyling = CtaItems?.length > 0 ? CtaItems[0]?.Expanded?.Styling : 'Button'
        setSpaceBetween({
            base: ctaStyling === 'Text' ? '16px' : '4px',
            lg: ctaStyling === 'Text' ? '20px' : '4px'
        })
    }, [CtaItems])

    // Landing Page Grid Layout
    if (shouldUseLandingPageLayout) {
        const backgroundImageUrl = CTABackgroundImage?.Expanded?.Url || CTABackgroundImage?.Url
        const hasBackground = CTABackgroundColor || backgroundImageUrl

        const renderItems = () => {
            return CtaItems?.map((item, index) => {
                const itemData = item?.Expanded || item
                const isIconWithText = itemData?.Styling === 'Icon With Text'
                // Check if this specific tile has a background color
                const itemHasBackground = itemData?.BackgroundColorPicker || itemData?.CTABackgroundColor
                const updatedItem = {
                    ...itemData,
                    CtaItemBlockWidth: '100%',
                    stylingButtonOverwrite: {width: '100%'},
                    // Don't add padding when tile has no background
                    noPadding: !itemHasBackground
                }
                return (
                    <Box
                        key={`cta-item-${index}`}
                        width="100%"
                        height="100%"
                        borderRadius="0"
                        overflow="hidden"
                        display="flex"
                        flexDirection="column"
                    >
                        {renderComponentsSwitch(updatedItem)}
                    </Box>
                )
            })
        }

        return (
            <Box
                data-block-type="CtaItemListBlock"
                width="100%"
                mb={hasBackground ? 0 : 12}
                py={{ base: hasBackground ? 8 : 0, lg: hasBackground ? 12 : 0 }}
                px={{ base: hasBackground ? 4 : 0, lg: hasBackground ? 8 : 0 }}
                pb={{ base: hasBackground ? 8 : 0, lg: hasBackground ? 12 : 0 }}
                position="relative"
                backgroundColor={CTABackgroundColor || 'transparent'}
                backgroundImage={backgroundImageUrl ? `url(${backgroundImageUrl})` : 'none'}
                backgroundSize="cover"
                backgroundPosition="center"
                backgroundRepeat="no-repeat"
                {...hideStyles}
            >
                <Box
                    maxWidth={{ base: '100%', lg: hasBackground ? 'container.lg' : '100%' }}
                    marginX="auto"
                    width="100%"
                >
                {Title && (
                    <Text
                        {...generateStyles(CtaItemListBlockTitleStyle, { ...styles.title })}
                        mb={{ base: 4, lg: 6 }}
                    >
                        {Title}
                    </Text>
                )}
                {Subtitle && (
                    <Text
                        {...generateStyles(CtaItemListBlockSubtitleStyle, { ...styles.subtitle })}
                        mb={{ base: 6, lg: 8 }}
                    >
                        {Subtitle}
                    </Text>
                )}
                {CtaItems?.length > 0 && (
                    <>
                        {isMobile ? (
                            // Mobile view: Carousel (default/empty) or Stacked based on CMS config
                            DisplayOption === 'Stacked' ? (
                                <Grid templateColumns="1fr" gap={gapValue != null ? `${gapValue}px` : '16px'}>
                                    {renderItems()}
                                </Grid>
                            ) : (
                                <Swiper
                                    spaceBetween={gapValue ?? 4}
                                    slidesPerView={2.1}
                                    freeMode={true}
                                    grabCursor={true}
                                >
                                    {renderItems().map((item, index) => (
                                        <SwiperSlide key={`swiper-${index}`}>{item}</SwiperSlide>
                                    ))}
                                </Swiper>
                            )
                        ) : (
                            <Grid templateColumns="repeat(3, 1fr)" gap={gapValue != null ? `${gapValue}px` : '32px'}>
                                {renderItems()}
                            </Grid>
                        )}
                    </>
                )}
                </Box>
            </Box>
        )
    }

    // Original Layout
    return (
        <Box {...global.notFullBleed} {...styles.container} {...hideStyles} data-block-type="CtaItemListBlock">
            <Text {...generateStyles(CtaItemListBlockTitleStyle, { ...styles.title }, { h2: { base: '20px', lg: '32px' } })}>
                {Title}
            </Text>
            <Box {...styles.wrapper} sx={{ ...styles.swiperCustom }}>
                <Swiper
                    modules={[Navigation]}
                    spaceBetween={spaceBetween.base}
                    freeMode={true}
                    slidesPerView='auto'
                    navigation={false}
                    grabCursor={true}
                    breakpoints={{
                        [lgBreakpoint]: {
                            spaceBetween: spaceBetween.lg,
                            navigation: {
                                prevEl: '.custom-swiper-prev',
                                nextEl: '.custom-swiper-next'
                            }
                        }
                    }
                    }>
                    {CtaItems?.map((CtaItemBlock, index) => {
                        const customStyling = {
                            stylingTextOverwrite: {
                                fontSize: { base: 'sm' },
                                lineHeight: { base: '100%', base: '16px' },
                                display: { base: 'inline-block' }
                            },
                            stylingOutlinedOverwrite: {
                                paddingTop: { base: '5px' },
                                paddingBottom: { base: '5px' },
                                paddingLeft: { base: '16px' },
                                paddingRight: { base: '16px' },
                                height: { base: 'auto' },
                                minHeight: { base: '32px' },
                                lineHeight: { base: '100%' },
                                fontSize: { base: 'sm' }
                            },
                            stylingButtonOverwrite: {
                                paddingTop: { base: '5px' },
                                paddingBottom: { base: '5px' },
                                paddingLeft: { base: '16px' },
                                paddingRight: { base: '16px' },
                                height: { base: 'auto' },
                                minHeight: { base: '32px' },
                                lineHeight: { base: '100%' },
                                fontSize: { base: 'sm' }
                            }
                        }

                        return <SwiperSlide
                            key={`CtaItemBlock-${index}`}
                            style={{ width: 'auto' }}
                            modules={[Navigation]}>
                            {renderComponentsSwitch(CtaItemBlock?.Expanded, { ...customStyling })}
                        </SwiperSlide>
                    })}
                    <ChevronLeftIcon display={{ base: 'none', lg: 'block' }} className='custom-swiper-prev' {...styles.swiperNav} {...styles.swiperNavLeft} />
                    <ChevronRightIcon display={{ base: 'none', lg: 'block' }} className='custom-swiper-next' {...styles.swiperNav} {...styles.swiperNavRight} />
                </Swiper>
            </Box>
        </Box>
    )
}

CtaItemListBlock.propTypes = {
    CtaItems: PropTypes.array,
    Title: PropTypes.string,
    Subtitle: PropTypes.string,
    CtaItemListBlockTitleStyle: PropTypes.object,
    CtaItemListBlockSubtitleStyle: PropTypes.object,
    CTABackgroundColor: PropTypes.string,
    CTABackgroundImage: PropTypes.object,
    isLandingPage: PropTypes.bool,
    CtaDivider: PropTypes.bool,
    HideOnDevices: PropTypes.string,
    DisplayOption: PropTypes.string,
    Gap: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    CtaGap: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
}

export default CtaItemListBlock
